import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Cpu, Database, Workflow, TrendingUp, Shield, Zap, Target } from 'lucide-react';
import { ArchitectIcon } from './CustomIcons';
import { Button } from './ui/button';
import { InteractiveCard } from './InteractiveCard';
import Footer from './Footer';

export default function AIImplementationPage() {
  useEffect(() => {
    document.title = 'AI Implementation Services - The Architect | Semando';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Custom AI solutions and machine learning implementations by The Architect. Intelligent systems that learn, adapt, and evolve with your business needs. £1,200/day.');
    }
  }, []);
  const offerings = [
    {
      icon: Cpu,
      title: 'Machine Learning Models',
      description: 'Custom ML solutions for predictive analytics, pattern recognition, and decision automation.'
    },
    {
      icon: Shield,
      title: 'AI Infrastructure',
      description: 'Scalable cloud-based AI infrastructure with optimal performance and cost efficiency.'
    },
    {
      icon: Database,
      title: 'Data Pipeline Automation',
      description: 'Intelligent data processing and ETL pipelines that learn and adapt over time.'
    },
    {
      icon: Workflow,
      title: 'Process Automation',
      description: 'AI-driven workflow automation that eliminates repetitive tasks and reduces errors.'
    }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Operational Efficiency',
      description: 'Dramatically reduce manual work through intelligent automation and predictive insights.'
    },
    {
      icon: Shield,
      title: 'Risk Mitigation',
      description: 'AI-powered threat detection and anomaly identification to prevent issues before they occur.'
    }
  ];

  return (
    <div className="min-h-screen space-y-0">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-background to-muted/20 relative">
        <div className="absolute inset-0 cyber-grid opacity-20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center px-4 py-2 bg-primary/10 rounded-full border border-primary/20">
                <ArchitectIcon className="h-5 w-5 text-primary mr-2" size={20} />
                <span className="text-sm text-primary">NEURAL_NETWORK_ACTIVE</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl tracking-tight">
                <span className="block text-muted-foreground text-xl md:text-2xl mb-4">THE ARCHITECT</span>
                Ideas are cheap. <br />Execution is everything.
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Every great system begins with a blueprint. Our AI architects don't just implement technology—they craft intelligent ecosystems that evolve, adapt, and transform your business from the ground up.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            <div className="text-center space-y-4">
              <h2 className="text-3xl md:text-4xl">What We Architect</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Comprehensive AI solutions designed with precision and built to scale with your vision.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {offerings.map((offering, index) => (
                <InteractiveCard key={index} className="hover-neon">
                  <div className="p-8 space-y-4">
                    <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-lg">
                      <offering.icon className="h-8 w-8 text-primary" size={32} />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-xl">{offering.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">{offering.description}</p>
                    </div>
                  </div>
                </InteractiveCard>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-24 bg-gradient-to-r from-muted/10 via-transparent to-muted/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl md:text-4xl">Why Every Business Needs an Architect</h2>
                <p className="text-xl text-muted-foreground leading-relaxed">
                  In the digital age, artificial intelligence isn't just code—it's the foundation upon which future success is built.
                </p>
              </div>
              
              <div className="space-y-6">
                <div className="space-y-2">
                  <h3 className="text-lg text-primary">Strategic Foundation</h3>
                  <p className="text-muted-foreground">
                    Like any great structure, AI implementations require careful planning, robust architecture, and forward-thinking design.
                  </p>
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg text-primary">Scalable Design</h3>
                  <p className="text-muted-foreground">
                    Our architectural approach ensures your AI systems grow and adapt with your business, never becoming constraints.
                  </p>
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg text-primary">Future-Proof Blueprint</h3>
                  <p className="text-muted-foreground">
                    We design AI ecosystems that anticipate tomorrow's challenges, ensuring your technology investment pays dividends for years.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="grid gap-6">
              {benefits.map((benefit, index) => (
                <InteractiveCard key={index}>
                  <div className="p-6 space-y-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                        <benefit.icon className="h-5 w-5 text-primary" />
                      </div>
                      <h3 className="text-lg">{benefit.title}</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
                  </div>
                </InteractiveCard>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Helps */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-16">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl">The Architect's Impact</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                When intelligence meets design, extraordinary outcomes emerge from carefully crafted foundations.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div className="space-y-4 p-6 rounded-lg border border-primary/20 bg-primary/5">
                <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-lg mx-auto">
                  <Zap className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg">Accelerated Processing</h3>
                <p className="text-muted-foreground">Intelligent architectures process complex decisions in real-time, transforming operational speed.</p>
              </div>
              <div className="space-y-4 p-6 rounded-lg border border-primary/20 bg-primary/5">
                <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-lg mx-auto">
                  <Target className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg">Precision Architecture</h3>
                <p className="text-muted-foreground">Every component designed with purpose, delivering unprecedented accuracy in pattern recognition.</p>
              </div>
              <div className="space-y-4 p-6 rounded-lg border border-primary/20 bg-primary/5">
                <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-lg mx-auto">
                  <Shield className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg">Continuous Evolution</h3>
                <p className="text-muted-foreground">Self-improving systems that learn and adapt, ensuring your architecture remains cutting-edge.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing & CTA */}
      <section className="py-24 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent relative">
        <div className="absolute inset-0 cyber-grid opacity-20" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8 relative z-10">
          <div className="space-y-4">
            <h2 className="text-3xl md:text-4xl">Work with The Architect</h2>
            <p className="text-xl text-muted-foreground">
              Daily rate for senior AI implementation architect.
            </p>
            <div className="text-6xl text-primary">$600<span className="text-2xl text-muted-foreground">/day</span></div>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Building intelligent systems requires vision, expertise, and meticulous attention to architectural detail.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild className="group bg-primary text-primary-foreground hover:bg-primary/90 cyber-glow">
              <Link to="/contact">
                Begin Your Blueprint
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}